// Quality against cost for one dataset, drawn as Task Replay's scatters (bench-scatter): each model at its agent
// cost per task and its combined rating, with a bar for the rating's 90% interval, and its short name beside it with
// labels on. With Pareto on, the frontier (no model both cheaper and rated higher) is dashed and its models ringed.
// Cost is on a log scale: the models compared cost tens of times apart.
import { esc } from '../core'
import { bareName, modelName, money } from './fmt'

export interface ParetoPoint {
  model: string
  cost: number
  elo: number
  low: number | null
  high: number | null
  tasks: number
}

/** Task Replay's first four series colours, then four more; the first six pass dataviz validate_palette.js on the
 *  app's paper (#fffdf9, light) with the fifth under 3:1, which the legend and the leaderboard relieve; the last two
 *  (brown, olive) are not yet validated. Colour follows the model: Sonnet, Opus and Haiku keep their slots on every
 *  dataset (as v1 shows them), others take the next ones in id order, and past eight they fold to gray. */
const SLOTS = ['#0f7a55', '#b8860b', '#b4452f', '#2f6f9f', '#e87ba4', '#4a3aa7', '#8c5a2b', '#5f7a1f']
const FAMILIES = ['sonnet', 'opus', 'haiku']
const FOLD = '#9a958b'

export function modelColors(models: string[]): Map<string, string> {
  const out = new Map<string, string>()
  const taken = new Set<number>()
  for (const m of [...models].sort()) {
    const f = FAMILIES.findIndex((x) => m.includes(x))
    if (f >= 0 && !taken.has(f)) {
      out.set(m, SLOTS[f]!)
      taken.add(f)
    }
  }
  let next = FAMILIES.length
  for (const m of [...models].sort()) {
    if (out.has(m)) continue
    while (taken.has(next)) next++
    out.set(m, SLOTS[next] ?? FOLD)
    taken.add(next)
  }
  return out
}

/** The models no other model beats on both cost and rating. */
export function frontier(points: ParetoPoint[]): Set<string> {
  return new Set(points.filter((p) => !points.some((q) => q !== p && q.cost <= p.cost && q.elo >= p.elo && (q.cost < p.cost || q.elo > p.elo)))
    .map((p) => p.model))
}

/** Ticks on a 1-2-5 sequence inside [lo, hi], for the log cost axis. */
function logTicks(lo: number, hi: number): number[] {
  const out: number[] = []
  for (let e = Math.floor(Math.log10(lo)); e <= Math.ceil(Math.log10(hi)); e++) {
    for (const m of [1, 2, 5]) {
      const v = m * 10 ** e
      if (v >= lo && v <= hi) out.push(v)
    }
  }
  return out
}

interface Box { x0: number; y0: number; x1: number; y1: number }
type Anchor = 'start' | 'middle' | 'end'

/** Where a line of 11px text sits, estimated (the SVG is a string, not yet drawn, so it can't be measured). */
function textBox(x: number, base: number, anchor: Anchor, s: string): Box {
  const w = s.length * 6.4
  const x0 = anchor === 'start' ? x : anchor === 'end' ? x - w : x - w / 2
  return { x0, y0: base - 9, x1: x0 + w, y1: base + 3 }
}

const overlap = (a: Box, b: Box): number =>
  Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0)) * Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0))

/** Each label beside its dot: right, left, a line up or down on either side, below or above, whichever first stays
 *  in `bounds` clear of `taken` (the dots, bars and ratings) and the labels placed before it; failing that, the least
 *  covered. `dots` come in placing order, each with the gap its label keeps from the centre. */
export function placeLabels(dots: { cx: number; cy: number; gap: number; text: string }[], taken: Box[], bounds: Box):
  { x: number; y: number; anchor: Anchor }[] {
  const placed: Box[] = []
  return dots.map((d) => {
    const g = d.gap, mid = d.cy + 4, line = 13
    const tries: [number, number, Anchor][] = [
      [d.cx + g, mid, 'start'], [d.cx - g, mid, 'end'],
      [d.cx + g - 3, mid - line, 'start'], [d.cx + g - 3, mid + line, 'start'],
      [d.cx - g + 3, mid - line, 'end'], [d.cx - g + 3, mid + line, 'end'],
      [d.cx, d.cy + g + 9, 'middle'], [d.cx, d.cy - g - 3, 'middle'],
    ]
    let best = tries[0]!, bestCost = Infinity
    for (const t of tries) {
      const b = textBox(t[0], t[1], t[2], d.text)
      const out = b.x0 < bounds.x0 || b.x1 > bounds.x1 || b.y0 < bounds.y0 || b.y1 > bounds.y1
      const cost = (out ? 1e9 : 0) + [...taken, ...placed].reduce((a, o) => a + overlap(b, o), 0)
      if (cost < bestCost) { best = t; bestCost = cost }
      if (cost === 0) break
    }
    placed.push(textBox(best[0], best[1], best[2], d.text))
    return { x: best[0], y: best[1], anchor: best[2] }
  })
}

/** The names beside the dots: without who a model's calls go through, unless two models would then read the same
 *  (one model run two ways). */
function shortNames(models: string[]): Map<string, string> {
  const short = models.map(bareName)
  return new Map(models.map((m, i) => [m, short.indexOf(short[i]!) === short.lastIndexOf(short[i]!) ? short[i]! : modelName(m)]))
}

export function scatter(points: ParetoPoint[], colors: Map<string, string>, pareto: boolean, labels = false): string {
  if (points.length < 2) return '<div class="empty">Needs two rated models.</div>'
  const W = 960, H = 300, padL = 48, padR = 16, padT = 14, padB = 46
  const pw = W - padL - padR, ph = H - padT - padB
  const xlo = Math.min(...points.map((p) => p.cost)) / 1.6, xhi = Math.max(...points.map((p) => p.cost)) * 1.6
  let ylo = Math.min(...points.map((p) => p.low ?? p.elo)), yhi = Math.max(...points.map((p) => p.high ?? p.elo))
  const step = [10, 20, 25, 50, 100, 200, 250, 500].find((st) => (yhi - ylo) / st <= 4) ?? 1000 // round gridlines
  ylo = Math.floor((ylo - step / 4) / step) * step
  yhi = Math.ceil((yhi + step / 4) / step) * step
  const sx = (v: number) => padL + ((Math.log10(v) - Math.log10(xlo)) / (Math.log10(xhi) - Math.log10(xlo))) * pw
  const sy = (v: number) => padT + (1 - (v - ylo) / (yhi - ylo)) * ph
  const front = frontier(points)

  let g = '<svg class="bench-scatter-svg" viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Rating against agent cost per task">'
  for (let yv = ylo; yv <= yhi; yv += step) {
    const py = sy(yv)
    g += '<line class="bench-scatter-grid" x1="' + padL + '" y1="' + py + '" x2="' + (padL + pw) + '" y2="' + py + '"/>'
    g += '<text class="bench-scatter-tick" x="' + (padL - 8) + '" y="' + (py + 4) + '" text-anchor="end">' + Math.round(yv) + '</text>'
  }
  for (const xv of logTicks(xlo, xhi)) {
    const px = sx(xv)
    g += '<line class="bench-scatter-grid" x1="' + px + '" y1="' + padT + '" x2="' + px + '" y2="' + (padT + ph) + '"/>'
    g += '<text class="bench-scatter-tick" x="' + px + '" y="' + (padT + ph + 18) + '" text-anchor="middle">' + esc(money(xv)) + '</text>'
  }
  g += '<text class="bench-scatter-axis" x="' + (padL + pw / 2) + '" y="' + (H - 6) + '" text-anchor="middle">Cost / task (log) →</text>'
  g += '<text class="bench-scatter-axis" x="14" y="' + (padT + ph / 2) + '" text-anchor="middle" transform="rotate(-90 14 ' + (padT + ph / 2) + ')">Rating →</text>'
  if (pareto) {
    const fs = points.filter((p) => front.has(p.model)).sort((a, b) => a.cost - b.cost)
    if (fs.length > 1) {
      g += '<polyline points="' + fs.map((p) => sx(p.cost).toFixed(1) + ',' + sy(p.elo).toFixed(1)).join(' ') +
        '" fill="none" stroke="#9a8c78" stroke-width="1.5" stroke-dasharray="2 4" stroke-linecap="round" stroke-opacity="0.75"/>'
    }
  }
  const r = 10
  const ringed = (p: ParetoPoint) => pareto && front.has(p.model)
  const taken: Box[] = []
  for (const p of points) {
    const c = colors.get(p.model) ?? FOLD
    const cx = sx(p.cost), cy = sy(p.elo), on = ringed(p)
    if (p.low != null && p.high != null && p.high > p.low) {
      g += '<line x1="' + cx + '" y1="' + sy(p.low) + '" x2="' + cx + '" y2="' + sy(p.high) + '" stroke="' + c + '" stroke-width="2" stroke-linecap="round" stroke-opacity="0.45"/>'
      taken.push({ x0: cx - 2, y0: sy(p.high), x1: cx + 2, y1: sy(p.low) })
    }
    if (on) g += '<circle cx="' + cx + '" cy="' + cy + '" r="' + (r + 4) + '" fill="none" stroke="' + c + '" stroke-opacity="0.6" stroke-width="2"/>'
    const tip = modelName(p.model) + '\nrating ' + Math.round(p.elo) + (p.low != null && p.high != null && p.high > p.low ? ' [' + p.low + ', ' + p.high + ']' : '') +
      ' · ' + money(p.cost) + ' / task · ' + p.tasks + ' tasks'
    g += '<circle class="tr-sc-dot" data-tip="' + esc(tip) + '" cx="' + cx + '" cy="' + cy + '" r="' + r + '" fill="' + c + '"/>'
    const ring = on ? r + 5 : r + 1
    taken.push({ x0: cx - ring, y0: cy - ring, x1: cx + ring, y1: cy + ring })
    if (on) {
      g += '<text class="tr-sc-num" x="' + cx + '" y="' + (cy - r - 6) + '" text-anchor="middle">' + Math.round(p.elo) + '</text>'
      taken.push(textBox(cx, cy - r - 6, 'middle', String(Math.round(p.elo))))
    }
  }
  if (labels) {
    // the frontier's first, then by rating: the names most looked for get the clearest places
    const order = [...points].sort((a, b) => Number(ringed(b)) - Number(ringed(a)) || b.elo - a.elo)
    const names = shortNames(points.map((p) => p.model))
    const dots = order.map((p) => ({ cx: sx(p.cost), cy: sy(p.elo), gap: (ringed(p) ? r + 4 : r) + 5, text: names.get(p.model)! }))
    const at = placeLabels(dots, taken, { x0: padL + 2, y0: 0, x1: W - 2, y1: padT + ph })
    g += dots.map((d, i) => '<text class="tr-sc-lab" x="' + at[i]!.x.toFixed(1) + '" y="' + at[i]!.y.toFixed(1) + '" text-anchor="' + at[i]!.anchor + '">' +
      esc(d.text) + '</text>').join('')
  }
  g += '</svg>'
  const note = '<div class="tr-sc-note">bar = 90% interval' + (pareto ? ' · ◯ on frontier' : '') + '</div>'
  const legend = '<div class="tr-scatter-vlegend">' + points.map((p) =>
    '<span class="tr-scatter-vkey' + (pareto && front.has(p.model) ? ' tr-sc-vkey-on' : '') + '"><span class="tr-scatter-vdot" style="background:' +
    (colors.get(p.model) ?? FOLD) + '"></span>' + esc(modelName(p.model)) + '</span>').join('') + '</div>'
  return g + note + legend
}

/** A switch with Task Replay's Pareto look ("Show Pareto", "Show labels"); `id` is wired by the caller. */
export function chartToggle(on: boolean, id: string, label: string): string {
  return '<span class="tr-pareto-ctl">' +
    '<button type="button" class="tr-pareto-btn' + (on ? ' on' : '') + '" id="' + id + '" role="switch" aria-checked="' + on + '"><span class="tr-pareto-knob"></span></button>' +
    '<span class="tr-pareto-lab">' + esc(label) + '</span></span>'
}
